import * as React from "react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  DeepMap,
  FieldError,
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Autocomplete, {
  AutocompleteRenderInputParams
} from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { useDebounce } from "hooks";
import { get, orderBy } from "lodash";

import { useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import {
  BUILTIN_INTAKE_FORM,
  BUILTIN_INTAKE_FORM_ID,
  CUSTOM_FORM_TEMPLATE_TYPE
} from "constants/form-templates";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import { useGetPracticeFormTemplatesQuery } from "services/form-templates";
import type { Practice } from "services/types/clinics.type";
import { FormTemplate } from "services/types/form-templates.type";

import FormTemplateListItem from "./form-template-list-item.component";

type FormTemplatesFieldType<T extends FieldValues> = {
  required?: boolean;
  type: number | number[];
  label: string;
  placeholder?: string;
  autofocus?: boolean;
  icon?: ReactNode;
  sx?: SxProps;
  initialSearch?: string;
  clearAfterSelect?: boolean;
  onSelected?: (product: FormTemplate) => void;
  disabled?: boolean;
  noLabel?: boolean;
  createYourOwnFormEnabled?: boolean;
  beforeSelect?: () => void;
  includeBuiltInIntakeForm?: boolean;
  disablePortal?: boolean;
} & UseControllerProps<T>;

export default function FormTemplatesField<T extends FieldValues>({
  control,
  name,
  required = false,
  label,
  placeholder,
  type,
  initialSearch = "",
  icon,
  autofocus = false,
  sx,
  clearAfterSelect = false,
  onSelected,
  disabled,
  noLabel = false,
  createYourOwnFormEnabled,
  beforeSelect,
  disablePortal = true,
  includeBuiltInIntakeForm
}: FormTemplatesFieldType<T>) {
  const currentPractice = useAppSelector(selectCurrentPractice);
  const [search, setSearch] = useState<string>(initialSearch);
  const [options, setOptions] = useState<FormTemplate[]>([]);
  const [open, setOpen] = useState<boolean>(autofocus);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { value: dSearch, isDebouncing } = useDebounce(search);
  const {
    field: { onChange, onBlur, ...restField },
    formState: { errors }
  } = useController<T>({
    name,
    control,
    rules: required
      ? {
          required: `${label} is required`
        }
      : undefined
  });
  const errorMessage =
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;

  const fieldValue = useWatch({ control, name });
  const renderInput = React.useCallback(
    ({ InputProps, ...restParams }: AutocompleteRenderInputParams) => (
      <TextField
        {...restParams}
        InputProps={{
          ...InputProps,
          startAdornment: icon ? (
            <InputAdornment position="start">{icon}</InputAdornment>
          ) : undefined
        }}
        sx={{ ml: 2, mt: 1, ...sx }}
        InputLabelProps={{ disabled: false }}
        required={required}
        error={Boolean(errorMessage)}
        helperText={errorMessage || ""}
        label={noLabel ? "" : label}
        autoFocus={open}
        placeholder={placeholder}
        disabled={disabled}
      />
    ),
    [
      errorMessage,
      open,
      sx,
      icon,
      placeholder,
      label,
      required,
      disabled,
      noLabel
    ]
  );

  const { data, isLoading, isSuccess } = useGetPracticeFormTemplatesQuery(
    {
      type,
      practice: currentClinic?.practice?.id
    },
    {
      skip: !Boolean(currentClinic?.practice?.id)
    }
  );

  const formTemplates = useMemo(() => {
    const searchValue = (dSearch ?? "").trim().toLowerCase();
    if (!searchValue.length) return data;

    if (fieldValue?.title?.toLowerCase()?.trim() === searchValue) {
      return data;
    }

    return data?.filter((item) =>
      item.title.toLowerCase().includes(searchValue)
    );
  }, [dSearch, data, fieldValue?.title]);

  useEffect(() => {
    if (Boolean(fieldValue)) {
      setSearch(fieldValue.title);
    } else {
      setSearch("");
    }
  }, [fieldValue]);

  useEffect(() => {
    if (formTemplates) {
      const baseOptions = orderBy(formTemplates, ["title"], ["asc"]);
      const builtinOptions = includeBuiltInIntakeForm
        ? [BUILTIN_INTAKE_FORM]
        : [];
      const additionalOptions = createYourOwnFormEnabled
        ? [
            {
              id: Number.MAX_SAFE_INTEGER,
              practice: currentPractice as Practice,
              type: CUSTOM_FORM_TEMPLATE_TYPE,
              title: "Create my Custom Form…",
              anvil_id: "",
              data_mapping: {}
            }
          ]
        : [];
      setOptions([...builtinOptions, ...baseOptions, ...additionalOptions]);
    } else {
      setOptions([]);
    }
  }, [
    currentPractice,
    formTemplates,
    createYourOwnFormEnabled,
    includeBuiltInIntakeForm
  ]);

  if (!isSuccess) {
    return <></>;
  }

  return (
    <Autocomplete
      disablePortal={disablePortal}
      loading={isLoading || isDebouncing}
      fullWidth
      disabled={disabled}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.title
      }
      renderOption={(props: object, option: FormTemplate) => (
        <FormTemplateListItem
          props={{
            ...props,
            sx:
              option.id === BUILTIN_INTAKE_FORM_ID
                ? { background: "#fffde7" }
                : option.type === CUSTOM_FORM_TEMPLATE_TYPE
                  ? { background: misc.lavenderMist }
                  : undefined
          }}
          option={option}
          key={option.id}
        />
      )}
      isOptionEqualToValue={(option: FormTemplate, value: FormTemplate) =>
        option.id === value.id
      }
      inputValue={search}
      onInputChange={(event, newInputValue, reason) => {
        if (Boolean(event)) {
          if (reason === "input" || reason === "reset") {
            setSearch(newInputValue);
          } else {
            setSearch("");
          }
        }
      }}
      popupIcon={<ArrowDropDownIcon />}
      renderInput={renderInput}
      filterOptions={(x) => x}
      onChange={(_, data) => {
        beforeSelect?.();
        onChange(data);
        if (Boolean(data)) {
          setSearch((data as FormTemplate).title || "");
          if (onSelected) {
            onSelected(data as FormTemplate);
            if (clearAfterSelect) {
              setSearch("");
            }
          }
        }
        onBlur();
      }}
      onBlur={onBlur}
      {...restField}
    />
  );
}
